import { Button, Col, Form, Image, Input, message, Row, Spin, Typography } from "antd";
import "./login.css";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getProductList, readClientId, readToken, removeClientId, setClientId, setToken, storeProductList } from "../../utils/DataService";
import { useNavigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import { getUserToken } from "../../api/auth";
import { MyContext } from "../../context/myContext";
import { getEntraConfig } from "../../api/EntraConfig";
import { getMsalInstance } from "./authConfig";
import { jwtDecode } from "jwt-decode";
import { IProduct } from "../../interfaces/IProduct";

const LoginPage: React.FC<any> = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const getUserTokenApi = useApi(getUserToken);
    const getEntraConfigApi = useApi(getEntraConfig);
    const { setIsAuthenticated, setUser, setSelectedProduct } = useContext(MyContext)
    const isMsalLogin = sessionStorage.getItem('msalLogin')
    
    const isUserValid = useMemo(()=>{
        return isMsalLogin || loading
    },[loading,isMsalLogin])

    useEffect(() => {
        // Handle SAML callback first
        const urlParams = new URLSearchParams(window.location.search);
        const samlToken = urlParams.get('token');
        const products = urlParams.get('products');
        const productList = urlParams.get('productList');

        if (samlToken && products && productList) {
            setLoading(true);
            try {
                // Store token
                setToken(samlToken);
                
                // Parse and store product list
                const parsedProducts = JSON.parse(products);
                const parsedProductList = JSON.parse(productList);
                
                const formattedProductList: IProduct[] = parsedProductList.map((item: any) => ({
                    name: item.name,
                    isSelected: false
                }));
                
                if (formattedProductList.length > 0) {
                    formattedProductList[0].isSelected = true;
                }
                
                storeProductList(JSON.stringify(formattedProductList));
                setSelectedProduct(formattedProductList[0]?.name);

                // Set user info from token
                const userInfo = jwtDecode<any>(samlToken);
                setUser(userInfo);
                setIsAuthenticated(true);

                // Clear URL parameters and navigate to root
                window.history.replaceState({}, document.title, '/');
                navigate("/");
                return; // Exit early after handling SAML callback
            } catch (error) {
                console.error('Error processing SAML callback:', error);
                message.error('Error processing login response');
                handleLogOut();
            } finally {
                setLoading(false);
            }
        }

        // Only check for existing token if we're not handling SAML callback
        const token = readToken();
        if (token) {
            navigate("/");
        }
    }, []);

    const loginToMsal = async (configs: any) => {

        try {
            setClientId(configs?.application_id);
            sessionStorage.setItem("msalLogin", "true");
            const msalInstance = getMsalInstance(configs?.application_id);
            const loginRequest = {
                scopes: ["User.Read"],
                loginHint: email
            };
            await msalInstance.loginRedirect(loginRequest)
        } catch (error) {
            sessionStorage.clear();
            localStorage.clear()
            message.error("Login failed. Please try again.");
        }
    };

    function extractDomainName(email: string) {
        const domain = email.split('@')[1];
        return domain;
    }

    const getEntraConfigDetail = async () => {
        try {
            const domainName = extractDomainName(email)
            const response = await getEntraConfigApi.request(domainName)
            if (response?.data?.entraConfig) {
                loginToMsal(response?.data?.entraConfig)
            } else {
                message.error(response?.response?.data?.message);
            }
        } catch (error) {
            message.error("Login failed. Please try again.");
        }
    }

    const handleLogOut = () => {
        sessionStorage.clear()
        localStorage.removeItem('productList')
        navigate('/login');
    };

    const handleLogin = async (idToken: string) => {
        try {
            const response = await getUserTokenApi.request(idToken);
            if (response?.data) {
                setToken(response?.data?.jwtToken);
                const storedProductList = getProductList()
                let prevSelectedProduct: IProduct;
                if(!response?.data?.productList?.length){
                    message.error("Product Not Found");
                }
                if (storedProductList) {
                    prevSelectedProduct = JSON.parse(storedProductList).find((product: any) => product.isSelected)

                }

                const productList: IProduct[] = response?.data?.productList.map((item: string, index: number) => ({
                    name: item,
                    isSelected: item === prevSelectedProduct?.name
                }));
                storeProductList(JSON.stringify(productList))
                setSelectedProduct((productList?.find((i: IProduct) => i.isSelected)?.name) || productList[0].name)

                const userInfo: any = jwtDecode<any>(response?.data?.jwtToken);

                removeClientId();
                setUser(userInfo)
                setIsAuthenticated(true);

                navigate("/");

            } else {
                message.error(response?.response?.data?.message);
                setLoading(false);
                setIsAuthenticated(false)
                handleLogOut();
            }
        } catch (err) {
            setLoading(false);
            setIsAuthenticated(false)
            handleLogOut();
        }
    };

    useEffect(() => {

        if (window.location.hash.includes("id_token") || window.location.hash.includes("code")) {
            setLoading(true);
            const msalInstance = getMsalInstance(readClientId());
            msalInstance
                .handleRedirectPromise()
                .then((loginResponse) => {
                    if (loginResponse) {

                        const accounts = msalInstance.getAllAccounts();
                        if (accounts.length > 0) {
                            msalInstance.setActiveAccount(accounts[0]);
                            if (loginResponse.idToken) {
                                handleLogin(loginResponse?.idToken)
                            } else {
                                setLoading(false)
                                handleLogOut();
                            }
                        }
                    }
                })
                .catch((error) => {
                    console.error("Error in handleRedirectPromise:", error);
                    sessionStorage.removeItem('msalLogin');
                    setLoading(false);
                    message.error("Something went wrong during the login process.");
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.hash]);



    const handleEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
    }, []);

   


    return isUserValid ? (
      <Row
        justify={"center"}
        align={"middle"}
        className="loading"
        data-testId="loading-indicator"
      >
        <Spin
          tip={
            <Typography.Title type="secondary" level={4}>
              Loading, please wait ...
            </Typography.Title>
          }
          size="large"
        ></Spin>
      </Row>
    ) : (
      <Row className="content">
        <Col className="h-100" span={24}>
          <Row className="header">
            <Col span={24} className="white-background">
              <Image
                preview={false}
                src={"/talentsavvy-logo-removebg.png"}
                width={"15%"}
              />
            </Col>
          </Row>
          <Form onFinish={() => getEntraConfigDetail()}>
            <Row className="login-screen-row">
              <Col span={8} className="form login-screen-column">
                <Row>
                  <Col span={24}>
                    <Typography.Title level={3} className="typography-color">
                      Welcome back to TalentSavvy!
                    </Typography.Title>
                  </Col>
                </Row>

                <>
                  <Row>
                    <Col span={24}>
                      <Typography.Text type="secondary">
                        Please enter you email address to sign in.
                      </Typography.Text>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24} className="mt-16">
                      <Form.Item
                        name={"email"}
                        rules={[
                          {
                            required: true,
                            type: "email",
                            message: "Please enter your email address.",
                          },
                        ]}
                      >
                        <Input
                          value={email}
                          onChange={handleEmailChange}
                          type="email"
                          placeholder="john@doe.com"
                          size="large"
                          data-testid="action-email"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24} style={{ marginTop: 8 }}>
                      <Button
                        data-testid="sign-in-button"
                        htmlType="submit"
                        className="signIn-button"
                      >
                        Sign in
                      </Button>
                    </Col>
                  </Row>
                </>
              </Col>
            </Row>
          </Form>
          <Row className="copyright" justify={"center"}>
            <Col className="centered-text">
              <Typography.Text strong>
                {" "}
                © TalentSavvy Inc. All rights reserved.
              </Typography.Text>
            </Col>
          </Row>
        </Col>
      </Row>
    );

}

export default LoginPage;
